import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import debounce from 'debounce-promise';
import { CancelToken, isCancel } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

import AssetService from '../../Services/AssetService';
import RuleService from '../../Services/RuleService';

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <FontAwesomeIcon icon={faCaretDown} className="text-muted" size="xs" />
  </components.DropdownIndicator>
);

export default function SearchByNameDropdown({
  assetView,
  handleAssetSelected,
  handleRuleSelected,
  includeChildren,
  mobileView,
  orgID,
  orgIdFilter,
  resource,
}) {
  const [error, setError] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const orgIdToUse = useMemo(
    () => (assetView ? orgID : orgIdFilter || orgID),
    [assetView, orgID, orgIdFilter]
  );
  const service = useMemo(
    () => (resource === 'Asset' ? new AssetService() : new RuleService()),
    [resource]
  );
  let source = CancelToken.source();

  const fetchAssets = async (q) => {
    try {
      setRequestLoading(true);
      setError('');
      const params = {
        children: includeChildren ?? false,
        limit: 50,
        order: 'ASC',
        orgID: orgIdToUse,
        q,
        sort: 'nickname',
      };

      source.cancel();
      source = CancelToken.source();
      const { data } = await service.searchAssets(params, source.token);
      setRequestLoading(false);
      return data.map(({ _id, nickname, orgID: gatewayOrgID }) => ({
        label: nickname,
        orgIdFilter: gatewayOrgID,
        value: _id,
      }));
    } catch (err) {
      setRequestLoading(false);
      if (isCancel(err)) return;
      const message = err.response?.data?.message ?? err.message;
      setError(message);
    }
  };

  const fetchRules = async (q) => {
    try {
      setRequestLoading(true);
      setError('');

      const params = {
        children: includeChildren ?? false,
        orgID: orgIdToUse,
        orgIdFilter: orgIdToUse,
        limit: 50,
        searchByName: q,
      };

      source.cancel();
      source = CancelToken.source();

      const { Rules } = await service.fetchPaginatedRules(params, source.token);
      setRequestLoading(false);
      return Array.isArray(Rules)
        ? Rules.map(({ Id, Name }) => {
            return { value: Id, label: Name };
          })
        : [];
    } catch (err) {
      setRequestLoading(false);
      if (isCancel(err)) return;
      const message = err.response?.data?.message ?? err.message;
      setError(message);
    }
  };

  const optionalBorder = mobileView
    ? { borderColor: '#dee2e6 !important' }
    : { border: 'none' };
  const mobileControlStyles = mobileView
    ? { height: '30px', minHeight: '30px' }
    : {};
  const mobileValueContainerStyles = mobileView
    ? {
        height: '30px',
        minHeight: '30px',
        position: 'static',
        lineHeight: '0.75rem',
      }
    : {};
  const mobileIndicatorContainerStyles = mobileView
    ? { paddingTop: '0.25rem', paddingRight: '0.4rem' }
    : {};
  const selectStyles = {
    placeholder: (base) => ({
      ...base,
      fontWeight: mobileView ? 'lighter' : 'normal',
      fontSize: '0.75rem',
      color: 'var(--text-gray)',
    }),
    control: (base) => ({
      ...base,
      ...optionalBorder,
      ...mobileControlStyles,
      outline: 'none',
      boxShadow: 'none',
    }),
    indicatorSeparator: (base) => ({ ...base, visibility: 'hidden' }),
    dropdownIndicator: (base) => ({
      ...base,
      ...mobileIndicatorContainerStyles,
    }),
    valueContainer: (base) => ({ ...base, ...mobileValueContainerStyles }),
    input: (base) => ({ ...base, ...mobileValueContainerStyles }),
  };

  const placeholder = mobileView ? 'Search for an asset' : resource;
  const mobileComponents = mobileView ? { DropdownIndicator } : {};
  return (
    <AsyncSelect
      components={{ ...mobileComponents }}
      isClearable
      isLoading={requestLoading}
      loadOptions={debounce(
        resource === 'Asset' ? fetchAssets : fetchRules,
        1000
      )}
      noOptionsMessage={() => `Search by ${resource} name...`}
      onChange={(selected) =>
        resource === 'Asset'
          ? handleAssetSelected(selected, mobileView)
          : handleRuleSelected(selected, mobileView)
      }
      placeholder={error || placeholder}
      styles={selectStyles}
    />
  );
}

SearchByNameDropdown.propTypes = {
  assetView: PropTypes.bool,
  handleAssetSelected: PropTypes.func,
  handleRuleSelected: PropTypes.func,
  includeChildren: PropTypes.bool,
  mobileView: PropTypes.bool,
  orgID: PropTypes.string,
  orgIdFilter: PropTypes.string,
  resource: PropTypes.oneOf(['Asset', 'Rule']),
};
